export const coursesData = {
  'scale-agency-mastery': {
    id: 'scale-agency-mastery',
    title: 'The Scale Agency: Product, SEO & Sales Blueprint',
    subtitle: 'The Definitive Trainee Guide: What We Sell, How We Deliver, and How to Close',
    description: 'Master our 5-channel search engine (SEO, GEO, AEO, Local, SXO), pricing packages, 45-minute discovery call architecture, and client fulfillment.',
    tag: 'Flagship Trainee Track',
    color: '#0ea5e9',
    estimatedHours: '4.5 Hours',
    chapters: [
      {
        id: 0,
        title: 'Module 1: The 2026 Search Shift & The Scale Agency Mission',
        content: `
          <div class="prose">
            <h2>The New Era of Organic Authority</h2>
            <p>Welcome to <strong>The Scale Agency</strong>. As a trainee on our team, your highest priority is to understand why our agency exists and why traditional SEO agencies are failing their clients in 2026.</p>
            
            <p>For two decades, the agency business model was simple: build backlinks, write keyword-stuffed articles, and chase "Rank #1" on Google's 10 blue links. That model is officially extinct.</p>

            <div class="stat-grid">
              <div class="stat-card">
                <span class="stat-n">65%+ Zero-Click Searches</span>
                <span class="stat-l">More than two-thirds of search queries are now resolved directly within AI snapshots or search answer boxes without the user ever clicking a link.</span>
              </div>
              <div class="stat-card">
                <span class="stat-n">AI Discovery Engines</span>
                <span class="stat-l">B2B buyers and consumers now use Perplexity, ChatGPT, Claude, and Gemini as their default search and research engines.</span>
              </div>
            </div>

            <div class="callout note">
              <div class="callout-label">The Scale Agency Mission</div>
              <div class="callout-text">
                We do not sell vanity keyword rankings. <strong>We build Omnipresent Digital Authority</strong> across the 5 modern search channels so our clients capture qualified revenue pipeline at every touchpoint of the buyer journey.
              </div>
            </div>

            <h3>The 5 Omnichannel Search Pillars We Operate</h3>
            <div class="step-flow">
              <div class="step-item">
                <div class="step-badge">1</div>
                <div class="step-content">
                  <div class="step-title">Organic SEO (The Technical Bedrock)</div>
                  <div class="step-desc">High-performance technical hygiene, Core Web Vitals, semantic topic clustering, and indexation architecture that ensures search bots can crawl without friction.</div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">2</div>
                <div class="step-content">
                  <div class="step-title">GEO (Generative Engine Optimization)</div>
                  <div class="step-desc">Engineering content and entity graphs so LLMs (ChatGPT, Perplexity, SearchGPT) cite our clients as the authoritative recommendation in conversational answers.</div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">3</div>
                <div class="step-content">
                  <div class="step-title">AEO (Answer Engine Optimization)</div>
                  <div class="step-desc">Direct answer extraction targeting Position Zero, Featured Snippets, and People Also Ask (PAA) accordions for zero-click dominance.</div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">4</div>
                <div class="step-content">
                  <div class="step-title">Local SEO & Geo-Relevance</div>
                  <div class="step-desc">Hyper-targeted Google Business Profile (GBP) dominance, citation velocity, and proximity clustering for commercial high-intent buyer actions.</div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">5</div>
                <div class="step-content">
                  <div class="step-title">SXO (Search Experience Optimization)</div>
                  <div class="step-desc">Bridging SEO and Conversion Rate Optimization (CRO). We turn incoming searchers into qualified booked discovery meetings and booked revenue.</div>
                </div>
              </div>
            </div>

            <h3>Who We Sell To: Our Ideal Customer Profile (ICP)</h3>
            <ul>
              <li><strong>B2B SaaS & Tech Scale-Ups:</strong> Founders and VPs of Marketing who have hit a ceiling with paid ad costs and need organic compounding revenue.</li>
              <li><strong>Mid-Market Professional Services:</strong> Law firms, consultancies, fintechs, and medical enterprises seeking dominant brand credibility and pipeline.</li>
              <li><strong>High-Ticket E-commerce & Direct-to-Consumer:</strong> Brands that need product category dominance across both search engines and AI assistant recommendations.</li>
            </ul>
          </div>
        `
      },
      {
        id: 1,
        title: 'Module 2: What We Sell — Products, Pricing & Retainers',
        content: `
          <div class="prose">
            <h2>The Scale Agency Commercial Portfolio</h2>
            <p>Every trainee must master our service catalog, how we price, and how each service maps directly to customer ROI. When talking to prospects, we never quote prices without diagnostic context.</p>

            <div class="callout tip">
              <div class="callout-label">Core Commercial Principle</div>
              <div class="callout-text">
                We never sell "hours" or "number of blog posts." We sell <strong>business outcomes, digital market share, and revenue attribution</strong>.
              </div>
            </div>

            <h3>1. The Tripwire: The Digital Transformation (DT) Audit</h3>
            <p><strong>Investment:</strong> $2,500 – $7,500 (One-time, credited toward monthly retainer if client signs within 14 days)</p>
            <ul>
              <li><strong>What it is:</strong> A forensic 360-degree diagnostic of the client's current digital footprint across all 5 channels.</li>
              <li><strong>Deliverables:</strong>
                <ul>
                  <li>Crawl Health & JavaScript Rendering Audit (identifying crawl budget waste).</li>
                  <li>AI Visibility & GEO Citation Gap Analysis (testing 50+ industry prompts across Perplexity and ChatGPT).</li>
                  <li>Entity Schema Graph Evaluation (JSON-LD structure and Knowledge Graph presence).</li>
                  <li>Revenue Vulnerability Scorecard & 90-Day Step-by-Step Strategic Roadmap.</li>
                </ul>
              </li>
              <li><strong>Sales Role:</strong> This is our high-margin entry point that eliminates objections and proves our technical superiority.</li>
            </ul>

            <h3>2. Growth Retainer: Core Omnichannel Search</h3>
            <p><strong>Investment:</strong> $5,000 / month (Minimum 6-month commitment)</p>
            <ul>
              <li><strong>Target:</strong> Growing companies ($2M - $10M ARR) ready to dominate their core niche.</li>
              <li><strong>Included:</strong>
                <ul>
                  <li>Complete Technical SEO hygiene maintenance and monthly Core Web Vitals sprints.</li>
                  <li>4 High-Impact Topical Authority Clusters per month (optimized for both human readers and LLM ingestion).</li>
                  <li>Core Schema Markup (Organization, Article, FAQPage, Service JSON-LD).</li>
                  <li>GEO Entity Establishment across Wikidata, Crunchbase, and brand citations.</li>
                  <li>Bi-weekly executive reporting with revenue attribution.</li>
                </ul>
              </li>
            </ul>

            <h3>3. Scale Retainer: Omnipresent Market Domination</h3>
            <p><strong>Investment:</strong> $9,500 / month (Our most popular engagement)</p>
            <ul>
              <li><strong>Target:</strong> Mid-market leaders ($10M - $50M ARR) wanting to aggressively displace competitors.</li>
              <li><strong>Included:</strong>
                <ul>
                  <li>Everything in Growth Retainer.</li>
                  <li>Full 5-Pillar Omnichannel Engine (SEO + GEO + AEO + Local + SXO).</li>
                  <li><strong>Parasite SEO Campaign:</strong> High-authority partner placements on Tier-1 news and publication domains for immediate ranking on ultra-competitive commercial keywords.</li>
                  <li>AEO Snippet & People Also Ask (PAA) weekly takeover sprints.</li>
                  <li>Conversion Rate Optimization (CRO) audit on top 10 Money Pages to maximize booked calls.</li>
                  <li>Dedicated Account Strategist, Lead Technical Architect, and Slack connect channel.</li>
                </ul>
              </li>
            </ul>

            <h3>4. Enterprise Custom Engine</h3>
            <p><strong>Investment:</strong> $15,000 – $30,000+ / month</p>
            <ul>
              <li>Multi-domain architecture, international/multi-lingual search, custom LLM fine-tuning data prep, and executive thought leadership syndication.</li>
            </ul>

            <div class="callout warn">
              <div class="callout-label">Pricing Guardrail</div>
              <div class="callout-text">
                Never discount our retainers. If a prospect pushes back on price, we reduce the scope of channels or deliverables—we never lower our price for the same deliverable. That preserves our premium positioning.
              </div>
            </div>
          </div>
        `
      },
      {
        id: 2,
        title: 'Module 3: The Scale Agency Sales Machine & Discovery Process',
        content: `
          <div class="prose">
            <h2>The 45-Minute Diagnostic Discovery Call</h2>
            <p>At The Scale Agency, we do not conduct "pitches" or show off 60-slide PowerPoint presentations. We conduct <strong>clinical diagnostic interviews</strong>.</p>
            
            <p>A prospect buys when they convince themselves that remaining in their current state is more expensive and painful than the cost of hiring us.</p>

            <div class="step-flow">
              <div class="step-item">
                <div class="step-badge">1</div>
                <div class="step-content">
                  <div class="step-title">The Reverse Agenda (0 - 5 Minutes)</div>
                  <div class="step-desc">
                    Establish control and set the frame. <em>"Today\'s goal isn\'t for me to pitch you. I want to understand your growth targets, dissect where your search pipeline is leaking, and if we are a mutual fit, we will map out next steps. Fair enough?"</em>
                  </div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">2</div>
                <div class="step-content">
                  <div class="step-title">Problem Identification (5 - 18 Minutes)</div>
                  <div class="step-desc">
                    Uncover technical friction and lead quality issues. Ask open-ended questions: <em>"When your team looks at inbound pipeline over the last 2 quarters, what trend is keeping leadership up at night?"</em>
                  </div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">3</div>
                <div class="step-content">
                  <div class="step-title">Implication & Cost of Inaction (18 - 32 Minutes)</div>
                  <div class="step-desc">
                    Quantify the financial bleeding. <em>"If you remain invisible in AI discovery engines for another 6 months while Competitor X captures those recommendations, how does that impact your $15M revenue target?"</em>
                  </div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">4</div>
                <div class="step-content">
                  <div class="step-title">The Solution Bridge & Assumptive Close (32 - 45 Minutes)</div>
                  <div class="step-desc">
                    Present the DT Audit as the only rational step. <em>"Rather than guessing on solutions, we run the diagnostic first. I will send over the DT Audit agreement for your team to sign today."</em>
                  </div>
                </div>
              </div>
            </div>

            <h2>Mastering the Top 5 Objections</h2>
            
            <div class="stat-grid">
              <div class="stat-card">
                <span class="stat-n">1. "We already have an SEO agency"</span>
                <span class="stat-l">
                  <strong>The Diagnostic Pivot:</strong> "Most agencies are fantastic at 2018 keyword rankings. Let me ask: when you query Perplexity or ChatGPT right now for [Prospect Niche], does your brand appear in the citations? If not, your current agency is ignoring 60% of modern search."
                </span>
              </div>
              <div class="stat-card">
                <span class="stat-n">2. "SEO takes too long to see results"</span>
                <span class="stat-l">
                  <strong>The Velocity Pivot:</strong> "Traditional SEO takes 9 months because people wait for domain authority to crawl up. That is why we include Parasite SEO and AEO sprints to capture immediate high-authority rank in weeks, while the compounding foundation builds."
                </span>
              </div>
              <div class="stat-card">
                <span class="stat-n">3. "AI is going to replace search anyway"</span>
                <span class="stat-l">
                  <strong>The GEO Moat Pivot:</strong> "You are 100% right that AI is disrupting Google. That is exactly why our clients hire us. Generative Engine Optimization (GEO) ensures that when AI answers questions, your brand is the sole verified source it cites."
                </span>
              </div>
              <div class="stat-card">
                <span class="stat-n">4. "Your retainers are higher than other quotes"</span>
                <span class="stat-l">
                  <strong>The Investment Frame:</strong> "You can easily find a $1,500/month provider who will generate 10 AI blogs and send a generic monthly PDF. If they bring zero attributable pipeline, that $1,500 is 100% waste. Our clients invest $9,500 because it drives $150k+ in pipeline."
                </span>
              </div>
            </div>

            <div class="callout tip">
              <div class="callout-label">The Oblique Rule of Closing</div>
              <div class="callout-text">
                Never defend your price. Inquire into their ROI. When they see the gap between their current reality and their revenue goal, our fee is the smallest number in the equation.
              </div>
            </div>
          </div>
        `
      },
      {
        id: 3,
        title: 'Module 4: Operational Fulfillment, Delivery & Quality Assurance',
        content: `
          <div class="prose">
            <h2>How The Scale Agency Executes for Clients</h2>
            <p>Closing the deal is only step one. Retaining clients for 24+ months requires flawless operational rigor. In this module, you learn our exact onboarding and fulfillment playbook.</p>

            <h3>The 4-Sprint Delivery Methodology</h3>
            
            <div class="step-flow">
              <div class="step-item">
                <div class="step-badge">Sprint 1</div>
                <div class="step-content">
                  <div class="step-title">Week 1–2: Foundation & Access Collection</div>
                  <div class="step-desc">
                    Google Search Console, Google Analytics 4, Tag Manager, CMS admin, and CDN/Cloudflare access secured. Deep technical audit executed; zero-value bloated URLs pruned to reclaim crawl budget.
                  </div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">Sprint 2</div>
                <div class="step-content">
                  <div class="step-title">Week 3–4: Entity Schema & Knowledge Graph Setup</div>
                  <div class="step-desc">
                    Deployment of enterprise JSON-LD markup: Organization, WebSite, Service, AboutPage, and Author Person schemas. Establishing canonical entity references across authoritative databases.
                  </div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">Sprint 3</div>
                <div class="step-content">
                  <div class="step-title">Month 2: High-Authority Topical Clusters & AEO Takeovers</div>
                  <div class="step-desc">
                    Publication of comprehensive topic authority clusters. Implementation of question-and-answer structural formats engineered to capture Featured Snippets and PAA blocks.
                  </div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">Sprint 4</div>
                <div class="step-content">
                  <div class="step-title">Month 3+: GEO Citations, Parasite Placements & SXO Conversion</div>
                  <div class="step-desc">
                    AI citation density building via digital PR and Tier-1 publisher syndication. Continuous heatmapping and SXO landing page optimization to elevate visit-to-meeting conversion rates.
                  </div>
                </div>
              </div>
            </div>

            <h3>How We Use Oblique Internally</h3>
            <p>Every trainee must know how Oblique powers our daily agency operations:</p>
            <ul>
              <li><strong>CRM Pipeline:</strong> Every deal flows through Stages: Lead Ingested &rarr; Discovery Completed &rarr; DT Audit Delivered &rarr; Proposal Sent &rarr; Closed Won.</li>
              <li><strong>QMS (Quality Management System):</strong> 100% of sales calls (via 3CX and Google Meet) are ingested and audited in Oblique QMS for objection handling compliance, adherence to the reverse agenda, and brand representation.</li>
              <li><strong>Client Retention Engine:</strong> Client health scores and performance milestones are monitored in Oblique to identify upsell or expansion opportunities before renewal dates.</li>
            </ul>

            <div class="callout note">
              <div class="callout-label">Next Step: Your Certification Exam</div>
              <div class="callout-text">
                Now that you have reviewed our service offerings, sales frameworks, and delivery processes, proceed to <strong>Module 5</strong> to complete your formal certification exam and practical assignments.
              </div>
            </div>
          </div>
        `
      },
      {
        id: 4,
        title: 'Module 5: Trainee Certification Exam & Practical Assignment',
        content: `
          <div class="prose">
            <h2>The Scale Agency Trainee Certification Exam</h2>
            <p>This is your official assessment to graduate from trainee to certified Agency Associate. This examination evaluates your command of our search channels, product catalog, sales psychology, and practical client scenarios.</p>

            <div class="callout tip">
              <div class="callout-label">Assessment Guidelines</div>
              <div class="callout-text">
                The exam consists of <strong>15 Multiple Choice Questions</strong> and <strong>3 Practical Written Case Studies</strong>. You must achieve at least <strong>80%</strong> to pass and receive your verified Certificate of Completion.
              </div>
            </div>
          </div>
        `,
        isExamChapter: true
      }
    ],
    exam: {
      id: 'scale-agency-exam-2026',
      title: 'The Scale Agency SEO, Product & Sales Certification Exam',
      description: 'Test your mastery of the 5 search channels, product packages, pricing tiers, discovery call framework, and practical client objections.',
      passingScore: 80,
      totalQuestions: 15,
      questions: [
        {
          id: 'q1',
          category: 'Market & Methodology',
          question: 'What is the primary difference between traditional SEO and Generative Engine Optimization (GEO)?',
          options: [
            'Traditional SEO focuses on backlinks, while GEO requires deleting all website pages.',
            'Traditional SEO optimizes for keyword rank on standard search engines; GEO optimizes for brand citation density and authority inside LLM answers (ChatGPT, Perplexity, Gemini).',
            'GEO is only applicable for paid Google Ads campaigns.',
            'There is no difference; GEO is simply an agency buzzword without technical backing.'
          ],
          correctIndex: 1,
          explanation: 'GEO structures content, entities, and citations so generative AI models trust and cite the brand as the definitive recommendation in conversational prompts.'
        },
        {
          id: 'q2',
          category: 'Search Channels',
          question: 'What does Answer Engine Optimization (AEO) specifically target in search engine results pages (SERPs)?',
          options: [
            'Organic blue link positions #8 through #10.',
            'Zero-click real estate such as Position Zero (Featured Snippets), Knowledge Panels, and People Also Ask (PAA) accordions.',
            'Paid shopping carousel ads only.',
            'Email newsletter spam folders.'
          ],
          correctIndex: 1,
          explanation: 'AEO targets position zero and rich results where over 65% of searchers get direct answers without clicking external links.'
        },
        {
          id: 'q3',
          category: 'Search Channels',
          question: 'In Search Experience Optimization (SXO), what two core disciplines are unified?',
          options: [
            'Social media management and cold telemarketing.',
            'Search Engine Optimization (SEO) and Conversion Rate Optimization (CRO).',
            'Graphic design and accounting software.',
            'Hardware maintenance and network cabling.'
          ],
          correctIndex: 1,
          explanation: 'SXO ensures that once searchers arrive via organic channels, their user experience, page speed, and conversion pathways maximize booked calls and reduce bounce rates.'
        },
        {
          id: 'q4',
          category: 'Commercial & Pricing',
          question: 'What is the commercial role and price range of The Scale Agency\'s Digital Transformation (DT) Audit?',
          options: [
            'It is a free PDF generator given away to any random email list.',
            'It is priced at $2,500 – $7,500 and serves as our diagnostic tripwire to uncover 5-channel gaps and seamlessly bridge to high-ticket monthly retainers.',
            'It costs $50,000 and is the only service we offer.',
            'It is an offshore manual data entry report.'
          ],
          correctIndex: 1,
          explanation: 'The DT Audit is our diagnostic entry point ($2.5k - $7.5k) that proves technical authority, builds immense trust, and naturally leads to a $5k - $9.5k/mo retainer.'
        },
        {
          id: 'q5',
          category: 'Commercial & Pricing',
          question: 'What is the monthly retainer investment for our flagship "Scale Retainer: Omnipresent Market Domination"?',
          options: [
            '$500 / month with no contract.',
            '$2,000 / month with 1 blog post.',
            '$9,500 / month including the full 5-Pillar Engine, Parasite SEO campaigns, AEO takeovers, and SXO optimization.',
            '$100,000 / month flat fee.'
          ],
          correctIndex: 2,
          explanation: 'The Scale Retainer is priced at $9,500/month and deploys the full 5-pillar omnichannel search engine with high-authority publication syndication.'
        },
        {
          id: 'q6',
          category: 'Commercial & Pricing',
          question: 'When a prospect asks for a discount on our monthly retainer fee, what is The Scale Agency\'s standard policy?',
          options: [
            'Immediately discount by 30% to secure the deal today.',
            'Never discount price for the same deliverable; instead, reduce the scope of channels or deliverables to maintain premium positioning.',
            'Offer free consulting for the first three months.',
            'Hang up the phone immediately without replying.'
          ],
          correctIndex: 1,
          explanation: 'We protect our margins and brand value. If budget is truly restricted, we adjust the scope of work (e.g., recommend the Growth Retainer or DT Audit) rather than discounting.'
        },
        {
          id: 'q7',
          category: 'Sales Methodology',
          question: 'During the first 5 minutes of a 45-minute discovery call, what is the sales rep\'s primary objective?',
          options: [
            'Present all 50 slides in the company sales pitch deck.',
            'Set the "Reverse Agenda" to take command, establish peer-to-peer positioning, and outline that the call is diagnostic rather than a sales pitch.',
            'Ask the prospect for their credit card number immediately.',
            'Apologize for taking up their busy time.'
          ],
          correctIndex: 1,
          explanation: 'The Reverse Agenda sets mutual expectations, creates a relaxed diagnostic atmosphere, and positions our sales reps as trusted strategic advisors.'
        },
        {
          id: 'q8',
          category: 'Sales Methodology',
          question: 'In the SPIN Selling framework, which question type is considered the "heavy hitter" that creates urgency for change?',
          options: [
            'Situation Questions ("What CMS are you on?").',
            'Implication Questions ("If this search traffic drop continues for another 6 months, how does that impact your revenue targets and hiring plans?").',
            'Small-talk questions about weather and sports.',
            'Billing address confirmation questions.'
          ],
          correctIndex: 1,
          explanation: 'Implication questions uncover the compounding financial consequences of doing nothing, making the cost of inactivity far greater than our agency fee.'
        },
        {
          id: 'q9',
          category: 'Objection Handling',
          question: 'How should a rep handle the objection: "We already have an SEO agency that charges us $1,500/month"?',
          options: [
            'Say that the other agency is incompetent and fraudulent.',
            'Pivot to the AI/GEO blindspot: Ask if their current agency has secured them citations in Perplexity or ChatGPT, explaining that cheap SEO ignores 60%+ of modern generative search.',
            'Match the competitor\'s price at $1,500/month.',
            'Agree with the client and politely end the call.'
          ],
          correctIndex: 1,
          explanation: 'The AI blindspot diagnostic highlights what standard low-cost agencies miss: they optimize for outdated rankings while the client remains invisible in generative search.'
        },
        {
          id: 'q10',
          category: 'Objection Handling',
          question: 'When a prospect insists: "Just email me a proposal and pricing PDF, and I\'ll review it later", what is the best response?',
          options: [
            'Send an unformatted pricing document immediately with no follow-up.',
            'Maintain control: Explain that because our roadmaps are diagnostic and customized to their unit economics, we walk through the audit and strategic plan together on a 20-minute review call.',
            'Tell them pricing is top secret and cannot be shared.',
            'Send them a link to Wikipedia.'
          ],
          correctIndex: 1,
          explanation: 'Proposals emailed without context are treated as price-comparison commodities. We always maintain collaborative control by reviewing the customized roadmap live.'
        },
        {
          id: 'q11',
          category: 'Technical Delivery',
          question: 'What is the purpose of "Crawl Budget Optimization" in our Sprint 1 technical delivery?',
          options: [
            'To make Google pay a fee every time it crawls the client\'s server.',
            'To eliminate thin, duplicate, or orphaned pages so search bots spend their crawling quota exclusively on high-conversion "Money Pages".',
            'To block all search engines from indexing the site.',
            'To slow down page load times intentionally.'
          ],
          correctIndex: 1,
          explanation: 'Search engine bots allocate a limited crawl budget per site. Pruning low-value bloat directs bot authority directly to the highest-converting commercial pages.'
        },
        {
          id: 'q12',
          category: 'Technical Delivery',
          question: 'What structured data schema type is vital for establishing our client\'s corporate entity in Google\'s Knowledge Graph?',
          options: [
            'Random HTML comment tags.',
            'JSON-LD Organization and WebSite markup with canonical "sameAs" references to Wikidata, LinkedIn, and Crunchbase.',
            'Inline CSS styling attributes.',
            'Plain text notes in the footer.'
          ],
          correctIndex: 1,
          explanation: 'JSON-LD schema with sameAs links maps the client\'s entity into Google\'s Knowledge Graph and LLM training corpora.'
        },
        {
          id: 'q13',
          category: 'Fulfillment & QA',
          question: 'How does The Scale Agency utilize Oblique QMS internally for sales operations?',
          options: [
            'As an offline spreadsheet for printing paper receipts.',
            'To automatically record, transcribe, and audit 100% of sales and discovery calls against strict objection handling and reverse agenda rubrics.',
            'To replace human sales reps with robot voices.',
            'It is not used at all.'
          ],
          correctIndex: 1,
          explanation: 'Oblique QMS ingests 3CX and Google Meet calls to objectively score reps on discovery depth, objection handling, and compliance.'
        },
        {
          id: 'q14',
          category: 'Closing Frameworks',
          question: 'What is an "Assumptive Close" in high-ticket agency sales?',
          options: [
            'Assuming the prospect will never buy and hanging up.',
            'Treating the contract agreement as the natural, logical next step of a diagnosed problem (e.g. "Should I send the DT Audit agreement to you or your finance lead?").',
            'Pressuring the client with fake expiring countdown timers.',
            'Demanding cash payment upfront.'
          ],
          correctIndex: 1,
          explanation: 'Assumptive closing naturally guides the prospect into the next logistical step without high-pressure friction, treating the solution as the agreed-upon remedy.'
        },
        {
          id: 'q15',
          category: 'Strategic Differentiation',
          question: 'Why do clients renew with The Scale Agency month after month?',
          options: [
            'Because we lock them into 10-year non-cancellable legal contracts.',
            'Because we connect search performance directly to revenue attribution, qualified pipeline, and dominant market authority rather than superficial vanity ranks.',
            'Because we send them free gift cards every Friday.',
            'Because search engines do not allow clients to switch agencies.'
          ],
          correctIndex: 1,
          explanation: 'Revenue attribution and genuine commercial pipeline growth are what retain enterprise clients, turning marketing from an expense into a high-ROI growth engine.'
        }
      ],
      assignments: [
        {
          id: 'case_1',
          title: 'Case Study 1: Re-positioning a B2B SaaS Losing Ground to AI Search',
          scenario: 'Prospect Profile: VP of Marketing at a $12M ARR Cloud Security SaaS company.\nSituation: Their organic traffic dropped 28% after Google rolled out generative AI overviews. Their current agency told them to "just wait it out." The VP is frustrated and considers SEO dead.',
          instructions: 'Draft your exact 3-step response on the discovery call:\n1. Acknowledge and reframe their traffic loss as a GEO/AEO opportunity.\n2. Formulate 2 high-impact Implication Questions.\n3. Transition smoothly to pitching our $5,000 DT Audit.',
          minWords: 60,
          rubric: 'Reframe traffic drop from catastrophe to AI citation shift; craft 2 implication questions focused on revenue impact; assumptive bridge to DT Audit.'
        },
        {
          id: 'case_2',
          title: 'Case Study 2: Objection Handling — The "$1,500/Month Agency" Challenge',
          scenario: 'Prospect Objection: "We like your vibe, but we have another proposal on the table for $1,500/month that promises 8 blog posts and 50 backlinks every month. You are asking for $9,500/month. Why should our CFO approve this?"',
          instructions: 'Write your verbatim dialogue script addressing this objection using the Diagnosis and ROI Implication method. Explain why quantity-based SEO is a liability and how our 5-pillar engine justifies the investment.',
          minWords: 60,
          rubric: 'Direct inquiry into expected pipeline from the cheap agency; contrast vanity deliverables (8 posts) with 5-channel authority; ROI calculation justification.'
        },
        {
          id: 'case_3',
          title: 'Case Study 3: Sprint 1 Client Onboarding 30-Day Plan',
          scenario: 'A newly closed client ($9,500/mo Scale Retainer) is eager to see immediate action during Week 1 of onboarding.',
          instructions: 'List the top 4 operational priorities your team executes during the first 30 days to build immediate momentum and prevent client buyer remorse.',
          minWords: 50,
          rubric: 'Access collection & crawl budget pruning; schema entity deployment; quick-win AEO snippet opportunities; transparent communication setup via Oblique.'
        }
      ]
    }
  },
  dt360: {
    id: 'dt360',
    title: 'DT 360° Mastery',
    subtitle: 'The Complete Digital Transformation & Modern Search Guide',
    description: 'Master the 5 search channels of 2026: Organic, AEO, GEO, Local, and SXO.',
    tag: 'Technical Foundation',
    color: '#6366f1',
    estimatedHours: '3.0 Hours',
    chapters: [
      {
        id: 0,
        title: 'Introduction: The Search Landscape of 2026',
        content: `
          <div class="prose">
            <h2>The Five Search Channels of 2026</h2>
            <p>Digital transformation is no longer just about having a website; it's about owning the digital footprint across the five primary search channels. Each channel requires a unique strategy and produces different buyer behaviors.</p>
            <ul>
              <li><strong>SEO (Google Organic):</strong> The compounding, evergreen foundation of digital growth. Organic traffic is an asset that appreciates; paid traffic is rent.</li>
              <li><strong>AEO (Answer Engine Optimization):</strong> Featured Snippets, PAA, and Knowledge Panels. 65%+ of searches now end without a click.</li>
              <li><strong>GEO (Generative Engine Optimization):</strong> The fastest-growing channel. AI models like ChatGPT and Perplexity are the new Rank #1.</li>
              <li><strong>Local SEO:</strong> Dominating "near me" intent. Critical for brick-and-mortar revenue.</li>
              <li><strong>SXO (Search Experience Optimization):</strong> The marriage of SEO and Conversion Rate Optimization.</li>
            </ul>
            <div class="callout note">
                <div class="callout-label">The Oblique Advantage</div>
                <div class="callout-text">A sales rep who understands these five channels can explain why traditional SEO is no longer enough and how we capture intent across the entire generative landscape.</div>
            </div>
            <h3>Why Intent Matters More Than Volume</h3>
            <p>Traditional marketing focused on "eyeballs." In 2026, we focus on "intent clusters." When a user asks an AI "What is the best CRM for a mid-market agency?", that intent is 10x more valuable than someone searching for "CRM features."</p>
          </div>
        `
      },
      {
        id: 1,
        title: 'SEO Fundamentals: The Technical Foundation',
        content: `
          <div class="prose">
            <h2>The Three Pillars of Modern SEO</h2>
            <p>To win in organic search, a site must excel in three distinct areas: Technical, Content, and Authority.</p>
            <div class="step-flow">
              <div class="step-item">
                <div class="step-badge">1</div>
                <div class="step-content">
                  <div class="step-title">Technical Integrity</div>
                  <div class="step-desc">Core Web Vitals, HTTPS, schema markup, and lean XML sitemaps. If Googlebot can't render your JS-heavy site, you don't exist.</div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">2</div>
                <div class="step-content">
                  <div class="step-title">Semantic Context</div>
                  <div class="step-desc">Moving beyond keywords to topics. Google's Hummingbird and BERT updates mean we write for meanings, not strings.</div>
                </div>
              </div>
              <div class="step-item">
                <div class="step-badge">3</div>
                <div class="step-content">
                  <div class="step-title">Brand Authority (E-E-A-T)</div>
                  <div class="step-desc">Experience, Expertise, Authoritativeness, and Trustworthiness. Citations from high-profile journals are the currency of trust.</div>
                </div>
              </div>
            </div>
            <h3>Crawl Budget Optimization</h3>
            <p>Large enterprise sites often waste "crawl budget" on low-value pages. Our strategy involves pruning thin content and redirecting internal link juice to high-conversion "Money Pages."</p>
          </div>
        `
      },
      {
        id: 2,
        title: 'GEO: Generative Engine Optimization',
        content: `
          <div class="prose">
            <h2>Winning the AI Citation War</h2>
            <p>GEO is the evolution of brand authority. AI models like Perplexity and SearchGPT don't just "rank" links; they "cite" sources they trust.</p>
            <div class="callout tip">
                <div class="callout-label">Sales Discovery Question</div>
                <div class="callout-text">"When I ask ChatGPT about your industry's problems, does your brand come up as the solution? If not, you are invisible to the next generation of buyers."</div>
            </div>
            <h3>The GEO Checklist</h3>
            <ul>
              <li><strong>Natural Language Processing (NLP):</strong> Structuring content in a Q&A format that LLMs find easy to ingest.</li>
              <li><strong>Citation Density:</strong> Ensuring your brand name is mentioned alongside industry keywords across the broad web.</li>
              <li><strong>Knowledge Graph Integration:</strong> Using JSON-LD to tell AI exactly what your product does.</li>
            </ul>
            <p>GEO is not about volume; it's about being the <strong>Definitive Answer</strong>. If an LLM summarizes your niche, your brand must be the primary source.</p>
          </div>
        `
      },
      {
        id: 3,
        title: 'AEO: Dominating Answer Engines',
        content: `
          <div class="prose">
            <h2>The Zero-Click Reality</h2>
            <p>Answer Engine Optimization (AEO) targets the 65% of users who get their answers directly on the search results page without clicking through.</p>
            <h3>Targeting Rich Results</h3>
            <ul>
              <li><strong>Position Zero:</strong> Designing content to trigger Featured Snippets.</li>
              <li><strong>People Also Ask (PAA):</strong> Dominating the Accordion of questions that Google suggests.</li>
              <li><strong>Knowledge Panels:</strong> Controlling the right-hand sidebar for brand searches.</li>
            </ul>
            <div class="callout note">
                <div class="callout-label">Deep Insight</div>
                <div class="callout-text">AEO builds brand recall. Even if they don't click, they see your name as the authority. When they ARE ready to buy, yours is the only name they remember.</div>
            </div>
          </div>
        `
      },
      {
        id: 4,
        title: 'Local SEO: Dominating Near-Me Intent',
        content: `
          <div class="prose">
            <h2>The Hyper-Local Strategy</h2>
            <p>For service businesses and retail, the Google Map Pack is more important than the organic 10-blue-links. Local SEO is a game of proximity, relevance, and prominence.</p>
            <ul>
              <li><strong>GBP Optimization:</strong> Maximum engagement on your Google Business Profile.</li>
              <li><strong>NAP Consistency:</strong> Name, Address, Phone number must be identical across 100+ directories.</li>
              <li><strong>Review Velocity:</strong> It's not just the rating; it's how fast you get new ones.</li>
            </ul>
          </div>
        `
      },
      {
        id: 5,
        title: 'SXO: The Conversion-Search Hybrid',
        content: `
          <div class="prose">
            <h2>Search Experience Optimization</h2>
            <p>SEO gets them to the door; SXO makes them walk in. Google now uses "dwell time" and "bounce rate" as ranking signals.</p>
            <h3>User Signals for Ranking</h3>
            <p>If a user clicks your result and immediately hits "back," Google demotes you. We optimize for the "Next Action."</p>
            <div class="callout warn">
              <div class="callout-label">Warning</div>
              <div class="callout-text">Slow load times and intrusive popups are now direct SEO penalties. A bad user experience is a bad search experience.</div>
            </div>
          </div>
        `
      }
    ]
  },
  sales: {
    id: 'sales',
    title: 'Sales Technique Mastery',
    subtitle: 'High-Ticket Agency Selling & Objection Defense',
    description: 'Master the Discovery-to-Close pipeline using modern psychological frameworks.',
    tag: 'Revenue Engine',
    color: '#10b981',
    estimatedHours: '2.5 Hours',
    chapters: [
      {
        id: 0,
        title: 'The Discovery Call Architecture',
        content: `
          <div class="prose">
            <h2>Positioning & The 45-Minute Discovery</h2>
            <p>The discovery call is not a pitch; it's a diagnostic search for pain. If there is no pain, there is no sale.</p>
            <h3>The 4-Part Discovery Structure</h3>
            <ol>
              <li><strong>The Opener (0-5m):</strong> Setting expectations and the "Reverse Agenda."</li>
              <li><strong>Problem Identification (5-15m):</strong> Using Open-Ended questions to find the technical gap.</li>
              <li><strong>Impact Analysis (15-30m):</strong> Quantifying the cost of doing nothing.</li>
              <li><strong>The Solution Bridge (30-45m):</strong> Teasing the strategy without giving the "How" for free.</li>
            </ol>
            <div class="callout warn">
                <div class="callout-label">Pro Tip</div>
                <div class="callout-text">The goal of Discovery is to make the prospect realize their current situation is unsustainable, not to show how smart you are.</div>
            </div>
          </div>
        `
      },
      {
        id: 1,
        title: 'Advanced SPIN Selling',
        content: `
          <div class="prose">
            <h2>The SPIN Framework Deep-Dive</h2>
            <p>Situation, Problem, Implication, Need-Payoff. This is The Scale Agency standard for high-ticket digital sales.</p>
            <h3>1. Situation Questions</h3>
            <p>"How much traffic are you seeing?", "What CMS are you running?" (Keep these to a minimum).</p>
            <h3>2. Problem Questions</h3>
            <p>"Are you happy with the lead quality?", "Where is the bottleneck in your content production?"</p>
            <h3>3. Implication Questions (The Heavy Hitters)</h3>
            <p>"If this search drop continues, how does it affect your Q4 revenue targets?", "What happens to the sales team if the pipeline stays dry?"</p>
            <h3>4. Need-Payoff Questions</h3>
            <p>"If we could automate 40% of that manual reporting, what would you do with that extra time?"</p>
          </div>
        `
      },
      {
        id: 2,
        title: 'BANT & Lead Qualification',
        content: `
          <div class="prose">
            <h2>Qualification is a Filter, Not a Gate</h2>
            <p>We don't waste time on leads that aren't a fit. Use BANT to prioritize your week.</p>
            <ul>
              <li><strong>Budget:</strong> Do they have the funds, or are they just "window shopping"?</li>
              <li><strong>Authority:</strong> Are you talking to the decision-maker or the researcher?</li>
              <li><strong>Need:</strong> Is their problem something we can actually solve profitably?</li>
              <li><strong>Timeline:</strong> Is the "fire" burning now or in 12 months?</li>
            </ul>
          </div>
        `
      },
      {
        id: 3,
        title: 'Objection Handling: The Diagnosis Method',
        content: `
          <div class="prose">
            <h2>Turning Rejection into Inquiry</h2>
            <p>Objections are just requests for more information. We use the <strong>Diagnosis Method</strong>.</p>
            <div class="stat-grid">
              <div class="stat-card">
                <span class="stat-n">Budget Concerns</span>
                <span class="stat-l">"It costs too much" -> Shift focus to ROI. "What happens to that $100k revenue gap if we do nothing?"</span>
              </div>
              <div class="stat-card">
                <span class="stat-n">Authority Deflection</span>
                <span class="stat-l">"I need to talk to my boss" -> "Understood. When you talk to them, what's the one thing they'll be most concerned about?"</span>
              </div>
            </div>
            <div class="callout note">
              <div class="callout-label">The Oblique Rule</div>
              <div class="callout-text">Never defend. Always inquire. A defense of your price confirms it's negotiable. An inquiry into their ROI confirms it's an investment.</div>
            </div>
          </div>
        `
      },
      {
        id: 4,
        title: 'Closing: The Assumptive Framework',
        content: `
          <div class="prose">
            <h2>The Art of the Next Step</h2>
            <p>In high-ticket sales, the "Close" is just the natural evolution of a solved problem.</p>
            <h3>Assumptive Closing</h3>
            <p>"Based on what we discussed, it makes sense to start with the DT Audit. Should I send the agreement to you or your operations lead?"</p>
          </div>
        `
      }
    ]
  },
  hubspot: {
    id: 'hubspot',
    title: 'HubSpot Mastery',
    subtitle: 'The CRM Ecosystem for Scale',
    description: 'Master the CRM object architecture and automation workflows.',
    tag: 'Ecosystem & Ops',
    color: '#f59e0b',
    estimatedHours: '2.0 Hours',
    chapters: [
      {
        id: 0,
        title: 'The Objects of HubSpot',
        content: `
          <div class="prose">
            <h2>The Four Core Objects</h2>
            <p>HubSpot is built on a relational database of four primary objects. Understanding their relationship is critical for data integrity.</p>
            <ul>
              <li><strong>Contacts:</strong> The individual humans (Unique ID: Email).</li>
              <li><strong>Companies:</strong> The parent legal entities (Unique ID: Domain Name).</li>
              <li><strong>Deals:</strong> Where the revenue lives (Lifecycle stages).</li>
              <li><strong>Tickets:</strong> Customer support and service tracking.</li>
            </ul>
            <p>Proper <strong>Association Mapping</strong> ensures that when a lead converts, their deal is linked to both the contact and the company, allowing for clean attribution reports.</p>
          </div>
        `
      },
      {
        id: 1,
        title: 'Automation & Workflow Strategy',
        content: `
          <div class="prose">
            <h2>Scale through Workflows</h2>
            <p>A "Workflow" is a series of automated actions triggered by a specific event. This is how we handle volume without increasing headcount.</p>
            <h3>Standard Workflow Patterns</h3>
            <ul>
              <li><strong>Lead Rotation:</strong> Evenly distributing leads across the sales team based on capacity.</li>
              <li><strong>Nurture Sequences:</strong> Sending 'Value-First' emails every 3 days until a meeting is booked.</li>
              <li><strong>Status Sync:</strong> Automatically moving a deal to 'Closed Won' when an e-signature is detected.</li>
            </ul>
          </div>
        `
      },
      {
        id: 2,
        title: 'Sales Hub: Sequencing & Tasks',
        content: `
          <div class="prose">
            <h2>Empowering the Sales Rep</h2>
            <p>Sequences are the secret weapon for outbound prospecting.</p>
            <p>A sequence is a 1-to-1 automated outreach that stops as soon as the prospect replies. This keeps the outreach feeling human while maintaining massive volume.</p>
          </div>
        `
      },
      {
        id: 3,
        title: 'Attribution & Reporting',
        content: `
          <div class="prose">
            <h2>Closing the Loop</h2>
            <p>Marketing spends money; Sales makes money. Attribution proves which spend resulted in which dollar.</p>
            <h3>First-Touch vs Last-Touch</h3>
            <p>Did they find us through the Blog (First Touch) or the LinkedIn Ad (Last Touch)? HubSpot Multi-Touch Attribution lets us see the entire journey.</p>
          </div>
        `
      }
    ]
  }
};
